import { useEffect, useRef, type ReactNode } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { STAFF_ME } from '@staff-app/graphql-client';
import type { AdminMe } from '@staff-app/shared-types';
import { auth, onAuthStateChanged, onIdTokenChanged } from '@/lib/firebase';
import { useAuthStore } from '@/stores/auth-store';

interface Props {
  children: ReactNode;
}

interface StaffMeResult {
  staffMe: AdminMe;
}

/**
 * Staff AuthProvider.
 *
 * Flow on sign-in:
 *   Firebase onAuthStateChanged fires with a @optisigns.com user
 *   → token captured into auth-store
 *   → Apollo `staffMe` query runs (authLink attaches the Bearer token)
 *   → backend AdminRoleGuard checks email ∈ admin_users
 *      - approved: returns { email, role, disabled: false } → adminUser populated
 *      - denied (403): adminUser = null → AdminRoleRoute renders PendingApprovalPage
 *
 * Unlike apps/web, there is no ME_QUERY / GET_COMPANY_INFO / iframe bridge /
 * SAML TTL check / orphan recovery. Staff users are pre-provisioned via
 * backend seeder + root-admin invite flow.
 */
export function AuthProvider({ children }: Props) {
  const apolloClient = useApolloClient();
  const setAuth = useAuthStore((s) => s.setAuth);
  const setToken = useAuthStore((s) => s.setToken);
  const setAdminUser = useAuthStore((s) => s.setAdminUser);
  const setAdminUserLoaded = useAuthStore((s) => s.setAdminUserLoaded);
  const setLoading = useAuthStore((s) => s.setLoading);
  const clearAuth = useAuthStore((s) => s.clearAuth);

  // Track the most recent token we've kicked off staffMe for — so dup calls
  // (React StrictMode double-mount) don't re-fire the query needlessly.
  const meRunForToken = useRef<string | null>(null);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        meRunForToken.current = null;
        clearAuth();
        return;
      }

      try {
        const token = await firebaseUser.getIdToken();
        setAuth({ firebaseUser, token });
      } catch (err) {
        console.error('[staff/AuthProvider] getIdToken failed', err);
        clearAuth();
      }
    });

    // Refresh token periodically so long-lived tabs stay authenticated.
    const unsubToken = onIdTokenChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) return;
      try {
        const token = await firebaseUser.getIdToken();
        setToken(token);
      } catch (err) {
        console.error('[staff/AuthProvider] onIdTokenChanged failed', err);
      }
    });

    return () => {
      unsubAuth();
      unsubToken();
    };
  }, [clearAuth, setAuth, setToken]);

  // Load staffMe whenever a new token lands.
  const token = useAuthStore((s) => s.token);
  useEffect(() => {
    if (!token) return;
    if (meRunForToken.current === token) return;
    meRunForToken.current = token;

    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await apolloClient.query<StaffMeResult>({
          query: STAFF_ME,
          fetchPolicy: 'network-only',
        });
        if (cancelled) return;
        if (error || !data?.staffMe) {
          // Non-throwing error path — e.g. schema mismatch — treat as denied.
          setAdminUser(null);
        } else {
          const me = data.staffMe;
          // Backend also rejects disabled users at the guard layer (403).
          // Defensive client-side check: treat disabled === true as denied.
          setAdminUser(me.disabled ? null : me);
        }
      } catch (err) {
        if (cancelled) return;
        // AdminRoleGuard returns 403 for non-admin and disabled users.
        // Any error → not approved. Show PendingApprovalPage.
        console.warn('[staff/AuthProvider] staffMe failed', err);
        setAdminUser(null);
      } finally {
        if (!cancelled) {
          setAdminUserLoaded(true);
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apolloClient, token, setAdminUser, setAdminUserLoaded, setLoading]);

  return <>{children}</>;
}
